import { cn } from "@/lib/utils";

// Base skeleton with shimmer effect
const SkeletonBase = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-md bg-muted/60",
      "before:absolute before:inset-0 before:-translate-x-full",
      "before:animate-[shimmer_2s_infinite]",
      "before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
      className
    )}
    {...props}
  />
);

// Card skeleton for services, pricing, etc.
export const CardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("glass-card rounded-2xl p-6 space-y-4", className)}>
    <SkeletonBase className="h-12 w-12 rounded-xl" />
    <SkeletonBase className="h-6 w-3/4" />
    <div className="space-y-2">
      <SkeletonBase className="h-4 w-full" />
      <SkeletonBase className="h-4 w-5/6" />
      <SkeletonBase className="h-4 w-4/6" />
    </div>
  </div>
);

// Blog post card skeleton
export const BlogCardSkeleton = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div className={cn("rounded-2xl overflow-hidden border border-border bg-card", className)} style={style}>
    <SkeletonBase className="h-48 w-full rounded-none" />
    <div className="p-6 space-y-3">
      <div className="flex items-center gap-2">
        <SkeletonBase className="h-5 w-20 rounded-full" />
        <SkeletonBase className="h-4 w-24" />
      </div>
      <SkeletonBase className="h-6 w-4/5" />
      <div className="space-y-2">
        <SkeletonBase className="h-4 w-full" />
        <SkeletonBase className="h-4 w-3/4" />
      </div>
    </div>
  </div>
);

// Stats skeleton
export const StatSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("text-center space-y-2", className)}>
    <SkeletonBase className="h-12 w-24 mx-auto rounded-lg" />
    <SkeletonBase className="h-4 w-32 mx-auto" />
  </div>
);

// Pricing card skeleton
export const PricingCardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("glass-card rounded-2xl p-8 space-y-6", className)}>
    <div className="space-y-2">
      <SkeletonBase className="h-6 w-1/3" />
      <SkeletonBase className="h-10 w-1/2" />
    </div>
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <SkeletonBase className="h-5 w-5 rounded-full" />
          <SkeletonBase className="h-4 flex-1" />
        </div>
      ))}
    </div>
    <SkeletonBase className="h-12 w-full rounded-xl" />
  </div>
);

// Text line skeleton
export const TextSkeleton = ({ lines = 3, className }: { lines?: number; className?: string }) => (
  <div className={cn("space-y-2", className)}>
    {[...Array(lines)].map((_, i) => (
      <SkeletonBase
        key={i}
        className="h-4"
        style={{ width: `${100 - (i * 10)}%` }}
      />
    ))}
  </div>
);

// Avatar skeleton
export const AvatarSkeleton = ({ size = "md", className }: { size?: "sm" | "md" | "lg"; className?: string }) => {
  const sizes = {
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-16 w-16"
  };
  
  return <SkeletonBase className={cn("rounded-full", sizes[size], className)} />;
};

// Table row skeleton
export const TableRowSkeleton = ({ cols = 4, className }: { cols?: number; className?: string }) => (
  <div className={cn("flex items-center gap-4 p-4", className)}>
    {[...Array(cols)].map((_, i) => (
      <SkeletonBase key={i} className="h-4 flex-1" />
    ))}
  </div>
);

// Image skeleton with aspect ratio
export const ImageSkeleton = ({ 
  aspectRatio = "16/9", 
  className 
}: { 
  aspectRatio?: string; 
  className?: string 
}) => (
  <SkeletonBase 
    className={cn("w-full", className)} 
    style={{ aspectRatio }} 
  />
);

// Full page loader with fade effect
export const PageLoader = ({ className }: { className?: string }) => (
  <div className={cn(
    "fixed inset-0 z-50 flex items-center justify-center",
    "bg-background/80 backdrop-blur-sm",
    "animate-fade-in",
    className
  )}>
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-4 border-primary/20" />
        <div className="absolute inset-0 h-12 w-12 rounded-full border-4 border-transparent border-t-primary animate-spin" />
      </div>
      <div className="text-sm text-muted-foreground animate-pulse">
        Loading...
      </div>
    </div>
  </div>
);

// Section loader
export const SectionLoader = ({ className }: { className?: string }) => (
  <div className={cn(
    "flex items-center justify-center py-12",
    className
  )}>
    <div className="flex items-center gap-2">
      <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

export { SkeletonBase as Skeleton };
